import { WatchfaceService } from '@/services/watchface';
import { validWatchfaceDir } from '@/utils/watchface-valid.utils';
import { ref } from 'vue';

interface WatchfaceStorageItem {
  path: string;
  // 是否为当前打开的状态 false为未打开，其他都为打开状态
  active?: boolean;
  // 是否在tab中显示
  tab?: boolean;
  // 上次打开的时间戳
  lastOpened?: number;
  // 是否为收藏态
  collect?: boolean;
}

export class WatchfaceStorage {
  static key = '_history';
  static data = ref<WatchfaceStorageItem[] | undefined>();

  private static resetStorage() {
    // 重新设置storage
    try {
      window.localStorage.setItem(
        WatchfaceStorage.key,
        JSON.stringify(WatchfaceStorage.data.value)
      );
    } catch (error) {
      console.log(error);
    }
    // console.log(WatchfaceStorage.data.value);
  }

  static async init() {
    const key = WatchfaceStorage.key;
    let items: WatchfaceStorageItem[] = [];
    try {
      const str = window.localStorage.getItem(key);
      items = str ? JSON.parse(str) : [];
    } catch (error) {
      console.log(error);
      items = [];
    }

    WatchfaceStorage.data.value = items;

    // 验证path是否合法
    items.forEach((item) => {
      const path = item.path;
      validWatchfaceDir(item.path, { ignoreDeviceType: true }).catch(() => {
        WatchfaceStorage.deleteItem(path);
      });
    });
  }

  /**
   * 新增表盘
   */
  static addItem(
    path: string,
    options?: {
      active?: WatchfaceStorageItem['active'];
      tab?: boolean;
    }
  ) {
    const { active, tab = true } = options ?? {};
    const items = WatchfaceStorage.data.value ?? [];
    // 如果不存在
    let item = items.find((_item) => _item.path == path);
    if (!item) {
      item = {
        path
      };
      items.push(item);
    }
    // active为互斥的，如果需要设置为true，需要将其他的设置为false
    if (active) {
      items.forEach((item) => (item.active = false));
    }
    // 如果当前没有在tabs中，则需要更新打开时间
    // if (!item.tab) item.lastOpened = Date.now();
    item.active = !!active;
    item.tab = !!tab;

    // 重新设置storage
    WatchfaceStorage.resetStorage();
    // console.log(item, WatchfaceStorage.data.value);
  }

  static deleteItem(path: string) {
    const items = WatchfaceStorage.data.value ?? [];
    const index = items.findIndex((item) => item.path == path);
    if (index > -1) items.splice(index, 1);

    // 重新设置storage
    WatchfaceStorage.resetStorage();
  }

  static updateItem(
    path: string,
    options: {
      tab?: boolean;
      active?: boolean;
      collect?: boolean;
      lastOpened?: number;
    }
  ) {
    const { tab, active, collect, lastOpened } = options;
    const items = WatchfaceStorage.data.value ?? [];
    const item = items.find((item) => item.path == path);
    if (!item) return;
    if (tab !== undefined) item.tab = !!tab;
    if (collect !== undefined) item.collect = !!collect;
    if (active !== undefined) {
      if (active) {
        items.forEach((item) => (item.active = false));
      }
      item.active = !!active;
    }
    if (lastOpened) item.lastOpened = lastOpened;

    // 重新设置storage
    WatchfaceStorage.resetStorage();
  }
  static replacePath(fromPath: string, toPath: string) {
    const items = WatchfaceStorage.data.value ?? [];
    const fromItem = items.find((item) => item.path == fromPath);
    const toItem = items.find((item) => item.path == toPath);
    if (!fromItem) {
      throw '原path不存在，无需replace';
    }

    // 如果已存在了一个目标path,则删除之
    if (toItem) {
      // delete
      WatchfaceStorage.data.value = WatchfaceStorage.data.value?.filter(
        (item) => item.path != toPath
      );
    }

    // 执行替换，将原path数据赋值给目标path，并删除原path
    fromItem.path = toPath;

    // 重新设置storage
    WatchfaceStorage.resetStorage();
  }

  /**
   * 清空所有的active状态
   */
  static clearActive() {
    WatchfaceStorage.data.value?.forEach((item) => (item.active = false));
    // 重新设置storage
    WatchfaceStorage.resetStorage();
  }
}

export interface HistoryWatchfaceItem extends WatchfaceStorageItem {
  title?: string;
  size?: string;
  preview?: string;
  deviceType?: string;
  isSample?: boolean;
}

const _watchfaceItems = ref<HistoryWatchfaceItem[]>([]);
export const useWatchfaceHistory = () => {
  const updateItems = async () => {
    // console.trace('updateItems');

    const items: HistoryWatchfaceItem[] = [];
    const storageItems = WatchfaceStorage.data.value ?? [];
    // _watchfaceItems.value = [...storageItems];
    for (let i = 0; i < storageItems.length; i++) {
      const item = storageItems[i];
      const service = new WatchfaceService(item.path);
      const description = await service.description.getDescriptionData();
      if (!description) continue;
      const itemTitle = description?.name;
      const title = String(itemTitle);
      const size = description?.size;
      const deviceType = description?.deviceType;
      const preview = '';

      items.push({
        ...item,
        size,
        title,
        deviceType,
        preview
      });
    }
    _watchfaceItems.value = items;
  };

  return {
    clearActive: WatchfaceStorage.clearActive,
    storageData: WatchfaceStorage.data,
    historyData: _watchfaceItems,
    deleteFromStorage: WatchfaceStorage.deleteItem,
    removeFromTabs: (path: string) => {
      WatchfaceStorage.updateItem(path, { tab: false, active: false });
    },
    addItem: WatchfaceStorage.addItem,
    updateItem: WatchfaceStorage.updateItem,
    updateItems,
    replacePath: WatchfaceStorage.replacePath
  };
};
